/**
 * Competitor referral tracker (tiny — 30 lines).
 *
 * Captures where a visitor heard about us so we can stamp it on the new org
 * record at signup time. Sources, in priority order:
 *   1. ?from=<vendor>  query param (e.g. /compare?from=givebutter)
 *   2. document.referrer hostname mapped to a known competitor
 *   3. nothing
 *
 * Stored in localStorage so it survives a user navigating through /tour →
 * /compare → /signup-org over multiple page loads.
 */
const KEY = "cf_signup_source";
const TTL_MS = 1000 * 60 * 60 * 24 * 30; // 30 days

const HOST_MAP = {
    "givebutter.com": "givebutter",
    "www.givebutter.com": "givebutter",
    "onecause.com": "onecause",
    "www.onecause.com": "onecause",
    "biddingforgood.com": "biddingforgood",
    "www.biddingforgood.com": "biddingforgood",
    "donorperfect.com": "donorperfect",
    "www.donorperfect.com": "donorperfect",
    "classy.org": "classy",
    "qgiv.com": "qgiv",
    "bloomerang.co": "bloomerang",
    "google.com": "google_search",
    "www.google.com": "google_search",
    "bing.com": "bing_search",
};

const VALID_VENDORS = new Set([
    "givebutter", "onecause", "biddingforgood", "donorperfect",
    "classy", "qgiv", "bloomerang", "google_search", "bing_search", "direct", "other",
]);

export function captureSignupSource() {
    if (typeof window === "undefined") return;
    try {
        // Don't overwrite a previously captured source — first-touch attribution
        const existing = readSignupSource();
        if (existing) return existing;

        const params = new URLSearchParams(window.location.search);
        const fromParam = (params.get("from") || "").trim().toLowerCase();
        let source = null;

        if (fromParam && VALID_VENDORS.has(fromParam)) {
            source = fromParam;
        } else if (document.referrer) {
            try {
                const refHost = new URL(document.referrer).hostname.toLowerCase();
                source = HOST_MAP[refHost] || null;
            } catch { /* invalid referrer */ }
        }
        if (!source) return null;

        const payload = { source, captured_at: new Date().toISOString(),
                          path: window.location.pathname };
        localStorage.setItem(KEY, JSON.stringify(payload));
        return source;
    } catch { return null; }
}

export function readSignupSource() {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;
        const obj = JSON.parse(raw);
        if (!obj?.source || !obj?.captured_at) return null;
        if (Date.now() - new Date(obj.captured_at).getTime() > TTL_MS) {
            localStorage.removeItem(KEY); return null;
        }
        return obj.source;
    } catch { return null; }
}

export function clearSignupSource() {
    try { localStorage.removeItem(KEY); } catch { /* noop */ }
}
